import { useQuery } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { queryKeys } from './query-client';
import type { Conversation } from './conversations';

// The conversation the user is IN, whether or not the sidebar list holds it.
//
// Everything around a chat reads its ROW — the header title, the header's pin / rename / delete, the recap
// above the composer — and the only source of rows was the sidebar list, which is one page of the account's
// conversations. So a chat the list does not hold lost all of it: the header read "New Symphony chat" and
// its menu dropped the three actions, on a conversation that has them. The list is still the fast path;
// this fetches the row only when the list cannot answer.

async function fetchConversation(conversationId: string): Promise<Conversation | null> {
  const res = await apiFetch(`${API_BASE}/api/conversations/${encodeURIComponent(conversationId)}`);
  if (!res.ok) return null;
  const body = (await res.json()) as { conversation?: Conversation | null };
  return body.conversation ?? null;
}

/**
 * `fromList` when the list holds the conversation, else the row fetched by id. Returns undefined while
 * there is no conversation to speak of (a brand-new chat), which is what keeps the row-scoped actions
 * absent rather than present and inert.
 */
export function useConversationRow(
  conversationId: string | undefined,
  fromList: Conversation | undefined,
): Conversation | undefined {
  // Disabled the moment the list can answer — the list is refreshed by the sidebar and by every write,
  // so a fetched copy must never be the one that wins.
  const enabled = Boolean(conversationId) && !fromList;
  const query = useQuery({
    queryKey: queryKeys.conversation(conversationId ?? undefined),
    queryFn: () => fetchConversation(conversationId as string),
    enabled,
  });
  return fromList ?? query.data ?? undefined;
}
